import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Loader2, CheckCircle, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import paymentModeService from '../../services/paymentModeService';
import subscriptionService from '../../services/subscriptionService';
import toast from 'react-hot-toast';

// Subscription Payment Modal Component using Razorpay Subscriptions
const SubscriptionPaymentModal = ({ 
  isOpen, 
  onClose, 
  paymentData, 
  onSuccess, 
  onFailure,
  title = "Complete Subscription Payment"
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'processing', 'success', 'failed'
  const [error, setError] = useState(null);
  const [step, setStep] = useState('init'); // 'init', 'checkout', 'success', 'failed'
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    // Reset state when modal closes
    if (!isOpen) {
      setError(null);
      setPaymentStatus(null);
      setLoading(false);
      setStep('init');
      hasInitializedRef.current = false;
      return;
    }
  }, [isOpen]);

  // Handle Razorpay script loading with error suppression
  useEffect(() => {
    const handleRazorpayError = (event) => {
      // Suppress RTB fingerprint header errors from Razorpay
      if (event.message && event.message.includes('x-rtb-fingerprint-id')) {
        event.preventDefault();
        return false;
      }
      
      // Suppress SVG attribute errors from Razorpay's checkout frame
      if (event.message && (
        event.message.includes('<svg> attribute width') ||
        event.message.includes('<svg> attribute height') ||
        event.message.includes('Unexpected end of attribute')
      )) {
        event.preventDefault();
        return false;
      }
      
      // Suppress errors from Razorpay's checkout frame
      if (event.filename && (
        event.filename.includes('checkout-frame.modern.js') ||
        event.filename.includes('checkout-static-next')
      )) {
        event.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (event) => {
      // Suppress unhandled promise rejections from Razorpay
      if (event.reason && (
        event.reason.message?.includes('<svg> attribute') ||
        event.reason.message?.includes('Unexpected end of attribute')
      )) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleRazorpayError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleRazorpayError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  const initializeSubscription = async () => {
    try {
      const razorpayConfig = paymentModeService.getRazorpayConfig();
      
      if (!razorpayConfig || !razorpayConfig.keyId) {
        setError('Payment system not initialized. Please contact support.');
        setPaymentStatus('failed');
        setStep('failed');
        setLoading(false);
        return;
      }

      // Check if Razorpay is loaded
      if (!window.Razorpay) {
        let attempts = 0;
        const maxAttempts = 30;
        while (!window.Razorpay && attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        if (!window.Razorpay) {
          setError('Payment system is loading. Please wait a moment and try again.');
          setPaymentStatus('failed');
          setStep('failed');
          setLoading(false);
          return;
        }
      }

      setLoading(true);
      setError(null);
      setPaymentStatus('processing');
      setStep('checkout');

      // Validate payment data
      if (!paymentData.amount || paymentData.amount <= 0) {
        throw new Error('Invalid payment amount');
      }

      // Get plan type from metadata
      const planType = paymentData.metadata?.plan || 'monthly';
      const planConfig = {
        monthly: { period: 'monthly', interval: 1 },
        quarterly: { period: 'monthly', interval: 3 },
        yearly: { period: 'monthly', interval: 12 }
      };

      const config = planConfig[planType] || planConfig.monthly;

      // Create or get Razorpay plan
      const planResult = await subscriptionService.getOrCreatePlan(planType, paymentData.amount);
      
      if (!planResult.success || !planResult.plan) {
        throw new Error(planResult.error || 'Failed to create subscription plan');
      }

      const planId = planResult.plan.id;

      // Create Razorpay subscription
      const subscriptionResult = await subscriptionService.createSubscription({
        planId: planId,
        customerNotify: 1,
        totalCount: 0, // Infinite subscription
        notes: {
          userId: paymentData.userId,
          coachingId: paymentData.metadata?.coachingId,
          plan: planType,
          planName: paymentData.metadata?.planName || planType,
          ...paymentData.metadata
        },
        customerDetails: {
          name: paymentData.customerName || '',
          email: paymentData.customerEmail || '',
          contact: paymentData.customerPhone || ''
        }
      });

      if (!subscriptionResult.success || !subscriptionResult.subscription) {
        console.error('Subscription creation failed:', subscriptionResult);
        throw new Error(subscriptionResult.error || 'Failed to create subscription');
      }

      const subscription = subscriptionResult.subscription;
      
      // Validate subscription object
      if (!subscription || !subscription.id) {
        console.error('Invalid subscription object:', subscription);
        throw new Error('Invalid subscription received from server');
      }
      
      // Log subscription details for debugging
      console.log('Subscription created:', {
        id: subscription.id,
        status: subscription.status,
        plan_id: subscription.plan_id,
        full_subscription: subscription
      });

      // Validate subscription ID format (should start with 'sub_')
      if (!subscription.id || !subscription.id.startsWith('sub_')) {
        throw new Error(`Invalid subscription ID format: ${subscription.id}`);
      }

      // Check if subscription is in a valid state for checkout
      if (subscription.status !== 'created' && subscription.status !== 'authenticated') {
        console.warn('Subscription status is:', subscription.status, '- proceeding anyway');
        // If status is not created, wait a moment and check again
        if (subscription.status === 'pending') {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Initialize Razorpay Subscription Checkout
      // For subscriptions, we MUST use subscription_id (not amount or order_id)
      // The subscription_id must be the full subscription object ID from Razorpay
      const options = {
        key: razorpayConfig.keyId,
        subscription_id: subscription.id, // Must be the subscription ID from Razorpay
        name: paymentData.merchantName || 'Mahstry Private Limited',
        description: paymentData.description || 'Subscription Payment',
        image: '/favicon.ico', // Add logo for better UX
        prefill: {
          name: paymentData.customerName || '',
          email: paymentData.customerEmail || '',
          contact: paymentData.customerPhone || ''
        },
        theme: {
          color: '#3B82F6'
        },
        handler: async function (response) {
          console.log('Subscription payment success:', response);
          await handleSubscriptionSuccess(response, subscription);
        },
        modal: {
          ondismiss: function() {
            console.log('Razorpay modal dismissed by user');
            handleSubscriptionFailure({
              error: {
                description: 'Subscription cancelled by user',
                reason: 'user_cancelled'
              }
            });
          }
        }
      };

      // Verify options before creating Razorpay instance
      if (!options.subscription_id) {
        console.error('Subscription ID missing in options:', options);
        throw new Error('Subscription ID is missing. Cannot initialize checkout.');
      }

      if (!options.subscription_id.startsWith('sub_')) {
        console.error('Invalid subscription ID format:', options.subscription_id);
        throw new Error(`Invalid subscription ID format: ${options.subscription_id}. Expected format: sub_*`);
      }

      console.log('Opening Razorpay subscription checkout with options:', {
        key: options.key ? 'present' : 'missing',
        subscription_id: options.subscription_id,
        subscription_id_format: options.subscription_id.startsWith('sub_') ? 'valid' : 'invalid',
        name: options.name
      });

      // Create Razorpay instance with subscription checkout
      const rzp = new window.Razorpay(options);
      
      // Add error handler for Razorpay - MUST be added before opening
      rzp.on('payment.failed', function (response) {
        console.error('=== RAZORPAY PAYMENT FAILED ===');
        console.error('Razorpay payment failed:', response);
        console.error('Payment failure details:', {
          error: response.error,
          metadata: response.metadata,
          subscription_id: response.subscription_id,
          full_response: JSON.stringify(response, null, 2)
        });
        handleSubscriptionFailure(response);
      });
      
      // Also catch any errors during checkout initialization
      rzp.on('error', function (error) {
        console.error('=== RAZORPAY ERROR EVENT ===');
        console.error('Razorpay error:', error);
        handleSubscriptionFailure({
          error: {
            description: error.message || 'An error occurred with the payment gateway',
            reason: 'razorpay_error'
          }
        });
      });
      
      // Add subscription-specific event handlers
      rzp.on('subscription.cancelled', function (response) {
        console.log('Subscription cancelled:', response);
        handleSubscriptionFailure({
          error: {
            description: 'Subscription was cancelled by user',
            reason: 'user_cancelled'
          }
        });
      });
      
      // Add handler for subscription authentication failure
      rzp.on('subscription.auth.failed', function (response) {
        console.error('Subscription authentication failed:', response);
        handleSubscriptionFailure({
          error: {
            description: 'Subscription authentication failed. Please try again.',
            reason: 'auth_failed'
          }
        });
      });
      
      // We've already verified subscription_id is in options before creating rzp instance
      // Razorpay doesn't expose options via rzp.options, so we trust our validation above
      console.log('Opening Razorpay checkout modal with subscription_id:', options.subscription_id);
      
      // Open the Razorpay checkout modal
      try {
        rzp.open();
        console.log('Razorpay modal opened successfully');
      } catch (openError) {
        console.error('Error opening Razorpay modal:', openError);
        handleSubscriptionFailure({
          error: {
            description: `Failed to open payment gateway: ${openError.message || 'Unknown error'}`,
            reason: 'modal_open_failed'
          }
        });
        return; // Don't continue if modal failed to open
      }
      
      setLoading(false);

    } catch (error) {
      console.error('Error in initializeSubscription:', error);
      console.error('Error stack:', error.stack);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        error: error
      });
      
      const errorMessage = error.message || 'Failed to initialize subscription';
      setError(errorMessage);
      setPaymentStatus('failed');
      setStep('failed');
      setLoading(false);
      
      if (onFailure) {
        console.log('Calling onFailure callback with error:', error);
        onFailure(error);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleSubscriptionSuccess = async (response, subscription) => {
    try {
      console.log('Subscription payment success response:', response);
      console.log('Subscription object:', subscription);
      
      setPaymentStatus('processing');
      setStep('success');
      
      // For subscriptions, we might get razorpay_subscription_id instead of razorpay_payment_id
      // The first payment might be included in the subscription response
      const paymentId = response.razorpay_payment_id || response.razorpay_subscription_id || response.razorpay_payment_link_id;
      
      // Validate that we have at least a subscription ID or payment ID
      if (!paymentId && !response.razorpay_subscription_id && !subscription?.id) {
        console.error('No payment ID or subscription ID in response:', response);
        throw new Error('Payment ID or Subscription ID not received from Razorpay');
      }

      // Create transaction data
      const transactionData = {
        subscriptionId: subscription.id,
        paymentId: paymentId || response.razorpay_subscription_id,
        orderId: response.razorpay_order_id || null,
        signature: response.razorpay_signature || null,
        amount: paymentData.amount,
        currency: paymentData.currency || 'INR',
        status: 'success',
        timestamp: new Date().toISOString(),
        paymentData: paymentData,
        userId: paymentData.userId || null,
        paymentType: 'coaching_subscription_autopay',
        merchantName: paymentData.merchantName || 'Mahstry Private Limited',
        customerName: paymentData.customerName || null,
        customerEmail: paymentData.customerEmail || null,
        customerPhone: paymentData.customerPhone || null,
        metadata: paymentData.metadata || {}
      };
      
      console.log('Transaction data prepared:', transactionData);

      setPaymentStatus('success');
      toast.success('Subscription activated successfully!');
      
      // Call success callback
      if (onSuccess) {
        onSuccess(response, transactionData);
      }

    } catch (error) {
      setError('Subscription activated but there was an error processing it. Please contact support.');
      setPaymentStatus('failed');
      setStep('failed');
      // Still try to call onSuccess if subscription was created
      if (onSuccess && response?.razorpay_subscription_id) {
        try {
          onSuccess(response, {
            subscriptionId: subscription.id,
            paymentId: response.razorpay_payment_id,
            amount: paymentData.amount,
            currency: paymentData.currency || 'INR',
            status: 'success',
            timestamp: new Date().toISOString()
          });
        } catch (callbackError) {
          // Error calling callback
        }
      }
    }
  };

  const handleSubscriptionFailure = (error) => {
    console.error('Subscription payment failure:', error);
    
    // Extract detailed error message
    let errorMessage = 'Payment failed. Please try again.';
    if (typeof error === 'string') {
      errorMessage = error;
    } else if (error?.error?.description) {
      errorMessage = error.error.description;
    } else if (error?.error?.reason) {
      errorMessage = error.error.reason;
    } else if (error?.message) {
      errorMessage = error.message;
    } else if (error?.description) {
      errorMessage = error.description;
    }
    
    setPaymentStatus('failed');
    setStep('failed');
    setError(errorMessage);
    setLoading(false);
    
    if (onFailure) {
      onFailure(error);
    } else {
      toast.error(errorMessage);
    }
  };

  if (!isOpen) return null;

  // Ensure paymentData exists
  if (!paymentData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Error</h3>
            <p className="text-gray-600 mb-4">Payment data is missing. Please try again.</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CreditCard className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Auto-renewing subscription</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && step === 'checkout' && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Opening payment gateway...</p>
            </div>
          )}

          {step === 'success' && (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Subscription Activated!</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Your subscription has been activated with autopay enabled. Payments will be automatically charged.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}

          {step === 'failed' && (
            <div className="text-center py-8">
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Subscription Failed</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">{error || 'Something went wrong. Please try again.'}</p>
              <div className="space-y-2">
                <button
                  onClick={initializeSubscription}
                  className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {step === 'init' && !loading && (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 dark:text-white mb-2">Subscription Details</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Amount:</span>
                    <span className="font-medium text-gray-900 dark:text-white">₹{paymentData?.amount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Plan:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {paymentData?.metadata?.planName || paymentData?.description || 'Subscription'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{paymentData?.customerName || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <RefreshCw className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <p className="font-medium">Auto-Renewal Enabled</p>
                    <p>Your subscription will automatically renew at the end of each billing cycle. You can cancel anytime.</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-green-800 dark:text-green-200">
                    <p className="font-medium">Secure Payment</p>
                    <p>Your payment is processed securely through Razorpay. We do not store your card details.</p>
                  </div>
                </div>
              </div>

              <button
                onClick={initializeSubscription}
                disabled={loading}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Activate Subscription - ₹{paymentData?.amount || 0}</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaymentModal;

